import { useEffect, useRef, useState } from 'react'

export interface Resource<T> {
  data: T | null
  error: unknown
  loading: boolean
}

/**
 * Holt Daten für eine Seite und räumt hinter sich auf.
 *
 * Statt einer Zustandsbibliothek: Jede Seite lädt, was sie zeigt. Der
 * AbortController ist dabei der eigentliche Grund für diesen Haken — wer
 * schnell durch die Sammlungen klickt, löst mehrere Anfragen aus, und ohne
 * Abbruch gewinnt die zuletzt eintreffende, nicht die zuletzt gewollte. Dann
 * steht auf der Seite ein Produkt, das man gar nicht geöffnet hat.
 */
export function useResource<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[]): Resource<T> {
  const [state, setState] = useState<Resource<T>>({ data: null, error: null, loading: true })

  // Die Ladefunktion wird bei jedem Rendern neu gebildet; als Abhängigkeit
  // würde sie eine Endlosschleife ergeben. Maßgeblich ist deps.
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const controller = new AbortController()
    let abgelöst = false

    setState({ data: null, error: null, loading: true })

    loadRef
      .current(controller.signal)
      .then((data) => {
        if (!abgelöst) setState({ data, error: null, loading: false })
      })
      .catch((error: unknown) => {
        if (abgelöst) return
        if (error instanceof DOMException && error.name === 'AbortError') return
        setState({ data: null, error, loading: false })
      })

    return () => {
      abgelöst = true
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return state
}
